<template>
  <v-container :fluid="true"
               :class="{ 'px-1': $vuetify.display.mdAndDown }"
               class="question-add pa-0"
  >
    <h1>
      <template v-if="isEditMode">
        {{ $t('project.views.QuestionAnswerEdit.title') }}
      </template>
      <template v-else>
        {{ $t('project.views.QuestionAnswerAdd.title') }}
      </template>
    </h1>

    <div v-if="isEditMode && questionPending" class="d-flex align-center justify-center h-100">
      <AppLoader />
    </div>
    <AppCard v-else class="my-4 px-12 question-add__form">
      <fieldset>
        <legend>1. {{ $t('project.views.QuestionAnswerAdd.steps.1') }}</legend>
        <div :style="{'max-width': '1000px'}">
          <VisualTextEditor :value="question ? question.description : newQuestion.description"
                            :placeholder="$t('project.views.QuestionAnswerAdd.description.placeholder')"
                            :errorMessages="descriptionErrors"
                            :max-length="DESCRIPTION_MAX_LENGTH"
                            clean-html-on-input
                            qa-variant
                            counter="footer"
                            @input="newQuestion.description = $event"
          />
        </div>
      </fieldset>

      <fieldset>
        <legend>2. {{ $t('project.views.QuestionAnswerAdd.steps.2') }}</legend>
        <div>
          <div v-if="newQuestion.file" class="d-flex align-center">
            <div class="d-flex align-center">
              <ClosdFildersIcon :document="newQuestion.file"/>
              <span v-if="newQuestion.file.numbering">{{ newQuestion.file.numbering }}</span>
              {{ newQuestion.file.basename }}
            </div>
            <AppTooltip>
              <template #activator="{ props }">
                <AppButton icon
                           variant="text"
                           density="comfortable"
                           class="ml-2"
                           v-bind="props"
                           @click="referenceDocumentDialogIsOpen = true"
                >
                  <font-awesome-icon :icon="['fal', 'pencil']"/>
                </AppButton>
              </template>
              <span>{{ $t('common.modify') }}</span>
            </AppTooltip>
            <AppTooltip>
              <template #activator="{ props }">
                <AppButton icon
                           variant="text"
                           density="comfortable"
                           v-bind="props"
                           @click="newQuestion.file = null"
                >
                  <font-awesome-icon :icon="['far', 'xmark']"/>
                </AppButton>
              </template>
              <span>{{ $t('common.delete') }}</span>
            </AppTooltip>
          </div>
          <div v-else :style="{'max-width': '280px'}">
            <AppButton variant="outlined"
                       block
                       color="primary"
                       @click="referenceDocumentDialogIsOpen = true"
            >
              <v-icon size="small" class="mr-2">far fa-plus</v-icon>
              {{ $t('common.select') }}
            </AppButton>
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>3. {{ $t('project.views.QuestionAnswerAdd.steps.3') }}</legend>
        <div :style="{'max-width': '590px'}">
          <QuestionAnswerCategoriesSelect :model-value="newQuestion.categories"
                                          @update:model-value="selectedCategoriesChanged"
          />
        </div>
      </fieldset>

      <fieldset>
        <legend>4. {{ $t('project.views.QuestionAnswerAdd.steps.4') }}</legend>
        <div :style="{'max-width': '590px'}">
          <QuestionAnswerAddShare v-model:custom-message="newQuestion.customShareMessage"
                                  v-model:share-type="newQuestion.shareType"
                                  :custom-message-validation="v$.newQuestion.customShareMessage"
                                  isEditMode
          />
        </div>
      </fieldset>

      <div class="text-right mt-4">
        <AppButton color="white"
                   exact-path
                   :to="{ name: QUESTION_ANSWER_LIST_ROUTE_NAME }"
                   class="mr-4"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton color="primary"
                   :loading="isEditMode ? patchQuestionPending : postQuestionPending"
                   @click="prepareSubmitQuestion"
        >
          <template v-if="isEditMode">
            {{ $t('project.views.QuestionAnswerEdit.saveModifications') }}
          </template>
          <template v-else>
            {{ $t('project.views.QuestionAnswerAdd.publish') }}
          </template>
        </AppButton>
      </div>
    </AppCard>

    <QuestionAnswerReferenceDocumentDialog v-if="referenceDocumentDialogIsOpen"
                                           :selected-document="newQuestion.file"
                                           @close="referenceDocumentDialogIsOpen = false"
                                           @validate-selection="document => newQuestion.file = document"
    />
  </v-container>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppLoader from '@/common/AppLoader.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import AppButton from '@/common/buttons/AppButton.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { track, USER_ROLE } from '@/common/pendo/agent'
import { formFieldErrors } from '@/common/utils/form'
import { getRoomRouteType } from '@/common/utils/rooms'
import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { toast } from '@/common/utils/toast'
import { maxEscapedLength } from '@/common/utils/validators'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import QuestionAnswerAddShare, { validations as addShareValidation } from '@/project/question-answer/QuestionAnswerAddShare.vue'
import QuestionAnswerCategoriesSelect from '@/project/question-answer/QuestionAnswerCategoriesSelect.vue'
import QuestionAnswerReferenceDocumentDialog from '@/project/question-answer/QuestionAnswerReferenceDocumentDialog.vue'
import { QUESTION_ANSWER_LIST_ROUTE_NAME, ROOM_MEMBERS_ROUTE_NAME, QUESTION_ANSWER_ITEM_ROUTE_NAME } from '@/router'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { QuestionAnswerActionTypes } from '@/store/modules/question-answer/action_types'
import { QuestionAnswerMutationTypes } from '@/store/modules/question-answer/mutation_types'

const DESCRIPTION_MAX_LENGTH = 32000

export default {
  name: 'QuestionAnswerForm',
  components: {
    AppButton,
    AppLoader,
    AppCard,
    AppTooltip,
    ClosdFildersIcon,
    QuestionAnswerAddShare,
    QuestionAnswerCategoriesSelect,
    QuestionAnswerReferenceDocumentDialog,
    VisualTextEditor,
  },
  setup: () => ({ v$: useVuelidate() }),
  validations: {
    newQuestion: {
      description: {
        required,
        maxLength: maxEscapedLength(DESCRIPTION_MAX_LENGTH),
      },
      ...addShareValidation,
    },
  },
  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    mnemo: {
      type: String,
      required: false,
    },
    questionId: {
      type: [Number, String],
      required: false,
    },
  },
  data () {
    return {
      DESCRIPTION_MAX_LENGTH,
      QUESTION_ANSWER_LIST_ROUTE_NAME,
      newQuestion: {
        description: '',
        file: null,
        status: 'draft',
        categories: [],
        customShareMessage: '',
        shareType: SHARE_TYPE_OPTIONS.CUSTOM,
      },
      referenceDocumentDialogIsOpen: false,
    }
  },
  head () {
    return {
      title: this.isEditMode
        ? this.$t('project.views.QuestionAnswerEdit.metaTitle')
        : this.$t('project.views.QuestionAnswerAdd.metaTitle'),
    }
  },
  computed: {
    ...mapState('questionAnswer', [
      'question',
      'questionPending',
      'postQuestionPending',
      'patchQuestionPending',
    ]),
    ...mapState('documentSharePicker', ['selectedGroups', 'selectedMembers']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['roomMnemo', 'isCurrentUserPm']),
    questionForm () {
      return this.v$.newQuestion
    },
    descriptionErrors () {
      return formFieldErrors(
        this.questionForm.description,
        [
          [
            'maxLength',
            this.$t('common.validations.maxLength', {
              fieldName: this.$t('project.views.QuestionAnswerAdd.description.label'),
              maxLength: DESCRIPTION_MAX_LENGTH,
            }),
          ],
          [
            'required',
            this.$t('common.validations.fieldRequired', {
              fieldName: this.$t('project.views.QuestionAnswerAdd.description.label'),
            }),
          ],
        ],
      )
    },
  },
  created () {
    if (!this.currentRoom.isDataroom) {
      this.$router.replace({
        name: ROOM_MEMBERS_ROUTE_NAME,
        params: { roomType: getRoomRouteType(this.currentRoom) },
      })
    } else if (this.isEditMode) {
      this.prepareGetQuestion()
    }
  },
  beforeUnmount () {
    if (this.isEditMode) {
      this.SET_QUESTION(null)
      this.RESET_SELECTED_GROUPS_AND_MEMBERS()
    }
  },
  methods: {
    ...mapActions('questionAnswer', [
      QuestionAnswerActionTypes.GET_QUESTION,
      QuestionAnswerActionTypes.POST_QUESTION,
      QuestionAnswerActionTypes.PATCH_QUESTION,
    ]),
    ...mapMutations('questionAnswer', [QuestionAnswerMutationTypes.SET_QUESTION]),
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      DocumentSharePickerActionTypes.RESET_SELECTED_GROUPS_AND_MEMBERS,
    ]),
    async prepareGetQuestion () {
      try {
        await this.GET_QUESTION({
          mnemo: this.mnemo,
          questionId: this.questionId,
        })

        this.newQuestion = {
          ...this.newQuestion,
          ...this.question,
          shareType: this.question.rights.isPublic
            ? SHARE_TYPE_OPTIONS.PUBLIC
            : SHARE_TYPE_OPTIONS.CUSTOM,
        }

        this.INIT_SELECTED_GROUPS(this.question.rights.groups)
        this.INIT_SELECTED_MEMBERS(this.question.rights.users)
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.views.QuestionAnswerItem.getQuestionError'))
        this.$router.push({ name: QUESTION_ANSWER_LIST_ROUTE_NAME })
      }
    },
    async prepareSubmitQuestion () {
      try {
        this.v$.$touch()
        if (this.v$.$invalid) {
          return
        }
        const { file, customShareMessage, ...questionRest } = this.newQuestion
        const questionObject = {
          ...questionRest,
          customMessage: customShareMessage,
          withGroups: this.selectedGroups.map(g => g.id),
          withUsers: this.selectedMembers.map(u => u.id),
        }
        if (file) {
          if (file.type === 'file') {
            questionObject.fileId = file.id
          } else if (file.type === 'folder') {
            questionObject.folderId = file.id
          }
        }

        if (this.isEditMode) {
          await this.PATCH_QUESTION({
            mnemo: this.roomMnemo,
            questionId: this.questionId,
            data: questionObject,
          })
          toast.success(this.$t('project.views.QuestionAnswerEdit.editQuestionSuccess'))
          this.$router.push({
            name: QUESTION_ANSWER_ITEM_ROUTE_NAME,
            params: {
              questionId: this.questionId,
            },
          })
        } else {
          const newQuestionData = await this.POST_QUESTION({
            mnemo: this.roomMnemo,
            data: questionObject,
          })
          toast.success(this.$t('project.views.QuestionAnswerAdd.postQuestionSuccess'))
          track('question_created', {
            project_mnemo: this.roomMnemo,
            user_role: this.isCurrentUserPm ? USER_ROLE.PROJECT_MANAGER : USER_ROLE.GUEST,
            question_id: newQuestionData.data.question.id,
          })
          this.$router.push({ name: QUESTION_ANSWER_LIST_ROUTE_NAME })
        }
      } catch (error) {
        console.error(error)
        if (this.isEditMode) {
          toast.error(this.$t('project.views.QuestionAnswerEdit.editQuestionError'))
        } else {
          toast.error(this.$t('project.views.QuestionAnswerAdd.postQuestionError'))
        }
      }
    },
    selectedCategoriesChanged (categories) {
      this.newQuestion.categories = categories
    },
  },
}
</script>

<style scoped lang="scss">
  .question-add {
    &__form {
      fieldset {
        border: none;

        &:not(:first-child) {
          margin-top: 32px;
        }

        legend {
          font-size: 16px;
          font-weight: 700;
          margin-bottom: 16px;
        }
      }
    }
  }
</style>
